import { max, sum } from 'lodash'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  BemCorrectionFactorsData,
  ConsumptionEnergyType,
} from '../../../generated/graphql'
import {
  energyCapacityPerAreaUnit,
  energyPerYearUnit,
  formatEnergy,
  formatPower,
  hourUnit,
  powerPerAreaUnit,
  powerUnit,
} from '../../../util/formatters'
import { FactorFormValues, mapInputToFactorFormValues } from './summary-util'
import { ControlledTextField } from '../../form/ControlledTextField'
import { validators } from '../../../validation/validators'
import { calcSumElectricityHourSeries } from '../../../energy-system/electricity-consumption-hour-series-calculator'
import { EmptyTableContainer } from './EmptyTableContainer'
import {
  formatEffectValue,
  formatSumValuePerArea,
  initTableData,
  isValidAndDirty,
  SummaryTableProps,
  SummaryTableRowData,
  useStyles,
} from './BemSummaryTable'

export function BemSummaryElectricityTable({
  model,
  hourSeries,
  sumSerie,
  correctionFactors,
  originalHourSeriesSums,
  onUpdateFactorData,
}: SummaryTableProps): React.ReactElement {
  const [tableData, setTableData] = useState<SummaryTableRowData[]>([])
  const [sumSerieMax, setSumSerieMax] = useState(0)
  const [additionalTableData, setAdditionalTableData] = useState<
    SummaryTableRowData[]
  >([])

  const { control, errors, formState, reset } = useForm<FactorFormValues>({
    defaultValues: mapInputToFactorFormValues(
      correctionFactors.filter(
        (f) =>
          f.energyType !== ConsumptionEnergyType.OccupantsElectricity &&
          f.energyType !== ConsumptionEnergyType.PropertyElectricity,
      ),
    ),
  })

  const { t } = useTranslation()
  const classes = useStyles()

  useEffect(() => {
    setTableData(
      initTableData(
        hourSeries.filter(
          (serie) =>
            serie.consumptionEnergyType !==
              ConsumptionEnergyType.OccupantsElectricity &&
            serie.consumptionEnergyType !==
              ConsumptionEnergyType.PropertyElectricity,
        ),
      ),
    )

    const sumHourSeries = calcSumElectricityHourSeries(
      model,
      hourSeries.find(
        (s) =>
          s.consumptionEnergyType === ConsumptionEnergyType.BaseLoadElectricity,
      )?.hourSerie ?? [],
      hourSeries.find(
        (s) =>
          s.consumptionEnergyType ===
          ConsumptionEnergyType.VentilationElectricity,
      )?.hourSerie ?? [],
      hourSeries.find(
        (s) =>
          s.consumptionEnergyType ===
          ConsumptionEnergyType.AppliancesElectricity,
      )?.hourSerie ?? [],
      hourSeries.find(
        (s) =>
          s.consumptionEnergyType === ConsumptionEnergyType.LightingElectricity,
      )?.hourSerie ?? [],
    )

    setAdditionalTableData(initTableData(sumHourSeries))
    setSumSerieMax(max(sumSerie) as number)
  }, [hourSeries, sumSerie, correctionFactors]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    reset(
      mapInputToFactorFormValues(
        correctionFactors.filter(
          (f) =>
            f.energyType !== ConsumptionEnergyType.OccupantsElectricity &&
            f.energyType !== ConsumptionEnergyType.PropertyElectricity,
        ),
      ),
    )
    void isValidAndDirty(undefined, formState, control)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const updateFactorData = async (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    energyType: ConsumptionEnergyType,
    fieldName: keyof Pick<
      BemCorrectionFactorsData,
      'correctionFactor' | 'energyFactor'
    >,
  ) => {
    if (await isValidAndDirty(event.target.name, formState, control)) {
      onUpdateFactorData(energyType, fieldName, event.target.value)
      reset(control.getValues())
    }
  }

  const getSumValueSum = () => sum(tableData.map((d) => d.sumValue))

  const getOriginalSumValueSum = () =>
    sum(
      tableData.map(
        (d) => originalHourSeriesSums[d.hourSeriesInfo.consumptionEnergyType],
      ),
    )

  const getEffectValueString = (
    sumValue: number,
    originalSumValue: number | undefined,
  ): string => {
    if (!originalSumValue || originalSumValue === 0) return '1'

    return formatEffectValue(sumValue / originalSumValue)
  }

  return (
    <>
      {tableData.length > 0 ? (
        <form>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>{energyPerYearUnit}</TableCell>
                  <TableCell>{energyCapacityPerAreaUnit}</TableCell>
                  <TableCell>{powerUnit}</TableCell>
                  <TableCell>{powerPerAreaUnit}</TableCell>
                  <TableCell>
                    {`${t(
                      'buildingEnergyModels.summary.maxValueUsageTime',
                    )}, ${hourUnit}`}
                  </TableCell>
                  <TableCell>
                    {t('buildingEnergyModels.summary.correctionFactor')}
                  </TableCell>
                  <TableCell>
                    {t('buildingEnergyModels.summary.energyCorrection')}
                  </TableCell>
                  <TableCell>
                    {t('buildingEnergyModels.summary.effect')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((data, index) => (
                  <TableRow
                    className={classes.tableRow}
                    key={index.toString()}
                    hover
                  >
                    <TableCell width="20%">
                      {data.hourSeriesInfo.hourSerieName}
                    </TableCell>
                    <TableCell>
                      {formatEnergy(data.sumValue, { omitUnit: true })}
                    </TableCell>
                    <TableCell>
                      {formatSumValuePerArea(
                        data.sumValue / (1000 * model.area),
                      )}
                    </TableCell>
                    <TableCell>
                      {formatPower(data.maxValue, { omitUnit: true })}
                    </TableCell>
                    <TableCell>
                      {formatSumValuePerArea(data.maxValue / model.area)}
                    </TableCell>
                    <TableCell>
                      {data.maxValue > 0
                        ? formatPower((data.sumValue / data.maxValue) * 1000, {
                            omitUnit: true,
                          })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <ControlledTextField
                        control={control}
                        name={`factorData.${index}.correctionFactor`}
                        error={errors.factorData?.at(index)?.correctionFactor}
                        rules={{
                          ...validators.required,
                          ...validators.copModifier,
                        }}
                        size="small"
                        onBlur={async (event) =>
                          updateFactorData(
                            event,
                            data.hourSeriesInfo.consumptionEnergyType,
                            'correctionFactor',
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <ControlledTextField
                        control={control}
                        name={`factorData.${index}.energyFactor`}
                        error={errors.factorData?.at(index)?.energyFactor}
                        rules={{
                          ...validators.required,
                          ...validators.decimal,
                        }}
                        size="small"
                        onBlur={async (event) =>
                          updateFactorData(
                            event,
                            data.hourSeriesInfo.consumptionEnergyType,
                            'energyFactor',
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {getEffectValueString(
                        data.sumValue,
                        originalHourSeriesSums[
                          data.hourSeriesInfo.consumptionEnergyType
                        ],
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {additionalTableData.map((data, index) => (
                  <TableRow
                    className={
                      index === 0
                        ? `${classes.doubleBorderTop} ${classes.tableRow}`
                        : classes.tableRow
                    }
                    key={index.toString()}
                    hover
                  >
                    <TableCell width="20%">
                      {data.hourSeriesInfo.hourSerieName}
                    </TableCell>
                    <TableCell>
                      {formatEnergy(data.sumValue, { omitUnit: true })}
                    </TableCell>
                    <TableCell>
                      {formatSumValuePerArea(
                        data.sumValue / (1000 * model.area),
                      )}
                    </TableCell>
                    <TableCell>
                      {formatPower(data.maxValue, { omitUnit: true })}
                    </TableCell>
                    <TableCell>
                      {formatSumValuePerArea(data.maxValue / model.area)}
                    </TableCell>
                    <TableCell>
                      {data.maxValue > 0
                        ? formatPower((data.sumValue / data.maxValue) * 1000, {
                            omitUnit: true,
                          })
                        : '-'}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell>
                      {getEffectValueString(
                        data.sumValue,
                        originalHourSeriesSums[
                          data.hourSeriesInfo.consumptionEnergyType
                        ],
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow
                  className={`${classes.doubleBorderTop} ${classes.tableRow}`}
                >
                  <TableCell className={classes.highlightFont}>
                    Yhteensä
                  </TableCell>
                  <TableCell className={classes.highlightFont}>
                    {formatEnergy(getSumValueSum(), { omitUnit: true })}
                  </TableCell>
                  <TableCell className={classes.highlightFont}>
                    {formatSumValuePerArea(
                      getSumValueSum() / (1000 * model.area),
                    )}
                  </TableCell>
                  <TableCell className={classes.highlightFont}>
                    {formatPower(sumSerieMax, { omitUnit: true })}
                  </TableCell>
                  <TableCell className={classes.highlightFont}>
                    {formatSumValuePerArea(sumSerieMax / model.area)}
                  </TableCell>
                  <TableCell className={classes.highlightFont}>
                    {sumSerieMax > 0
                      ? formatPower((getSumValueSum() / sumSerieMax) * 1000, {
                          omitUnit: true,
                        })
                      : '-'}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell className={classes.highlightFont}>
                    {getEffectValueString(
                      getSumValueSum(),
                      getOriginalSumValueSum(),
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </form>
      ) : (
        <EmptyTableContainer
          text={t('buildingEnergyModels.summary.noTableData')}
        />
      )}
    </>
  )
}
